import { useEffect, useRef, useState, type KeyboardEvent, type PointerEvent, type RefObject } from 'react'
import {
  ASSISTANT_PANEL_WIDTH,
  assistantPanelMaxWidth,
  clampAssistantPanelWidth,
  readAssistantPanelWidth,
  writeAssistantPanelWidth
} from '@/lib/assistantShell'

const viewportWidth = () => (typeof window === 'undefined' ? 1440 : window.innerWidth)

export function useAssistantPanelWidth(panelRef?: RefObject<HTMLElement | null>) {
  const [stored, setStored] = useState<number | null>(readAssistantPanelWidth)
  const [viewport, setViewport] = useState(viewportWidth)
  const [resizing, setResizing] = useState(false)
  const drag = useRef<{ startX: number; startWidth: number; width: number } | null>(null)

  useEffect(() => {
    const onResize = () => setViewport(viewportWidth())
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  useEffect(() => {
    if (!resizing) return
    const previous = document.body.style.cursor
    document.body.style.cursor = 'col-resize'
    return () => {
      document.body.style.cursor = previous
    }
  }, [resizing])

  const width = stored == null ? null : clampAssistantPanelWidth(stored, viewport)
  const currentWidth = () => width ?? panelRef?.current?.getBoundingClientRect().width ?? ASSISTANT_PANEL_WIDTH.default

  const save = (next: number | null) => {
    setStored(next)
    writeAssistantPanelWidth(next)
  }

  const onPointerDown = (event: PointerEvent<HTMLElement>) => {
    if (event.button !== 0) return
    event.preventDefault()
    event.currentTarget.setPointerCapture(event.pointerId)
    const startWidth = currentWidth()
    drag.current = { startX: event.clientX, startWidth, width: startWidth }
    setResizing(true)
  }

  const onPointerMove = (event: PointerEvent<HTMLElement>) => {
    const current = drag.current
    if (!current) return
    current.width = clampAssistantPanelWidth(current.startWidth + current.startX - event.clientX, viewportWidth())
    setStored(current.width)
  }

  const onPointerEnd = () => {
    const current = drag.current
    if (!current) return
    drag.current = null
    setResizing(false)
    if (current.width !== current.startWidth) save(current.width)
  }

  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    const delta = event.key === 'ArrowLeft' ? ASSISTANT_PANEL_WIDTH.step : event.key === 'ArrowRight' ? -ASSISTANT_PANEL_WIDTH.step : 0
    if (!delta) return
    event.preventDefault()
    save(clampAssistantPanelWidth(currentWidth() + delta, viewportWidth()))
  }

  return {
    width,
    resizing,
    handleProps: {
      role: 'separator' as const,
      tabIndex: 0,
      'aria-orientation': 'vertical' as const,
      'aria-label': 'Redimensionar o assistente',
      'aria-valuemin': ASSISTANT_PANEL_WIDTH.min,
      'aria-valuemax': assistantPanelMaxWidth(viewport),
      'aria-valuenow': Math.round(width ?? ASSISTANT_PANEL_WIDTH.default),
      title: 'Arraste para redimensionar · duplo clique volta ao tamanho padrão',
      onPointerDown,
      onPointerMove,
      onPointerUp: onPointerEnd,
      onPointerCancel: onPointerEnd,
      onLostPointerCapture: onPointerEnd,
      onKeyDown,
      onDoubleClick: () => save(null)
    }
  }
}
